import { createSlice } from '@reduxjs/toolkit';
import authEndpoints from 'app/api/services/authEndpoints';
import auth from 'app/api/auth';
import { setFromLogin } from './settings';
import userEndpoints from 'app/api/services/userEndpoints';
import {
    clearActiveCompany,
    clearCompanyInfractions,
    clearCompanyLicenses,
    clearCompanyLines,
    clearCompanyVehicles,
    setActiveCompany,
} from './company';
import companyEndpoints from 'app/api/services/companyEndpoints';
import { errorHandler } from 'app/utils/errorHandler';
import { clearServices } from './services';
import { clearRequests } from './requests';
import {
    clearAllLines,
    clearLineScheduleBoard,
    clearPermissionaries,
    clearSchedulesStore,
    clearSelectedEquipmentClassification,
    clearSelectedEquipmentType,
    clearSelectedLineId,
    clearSelectedSubstituteEquipmentClassification,
    clearSelectedSubstituteEquipmentType,
} from './line';

/**
 *
 * @type {{activeInactiveUsers: *[], registrationInfo: RegistrationUserInfo, userAdmin: {}, user: {name: string, email: string}, users: *[]}}
 */
const initialState = {
    user: {
        name: 'Visitante',
        email: 'Visitante',
    },
    registrationInfo: {},
    users: [],
    activeInactiveUsers: [],
    userAdmin: {},
};

const userSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        getUser: (state, action) => {
            state.user = action.payload;
        },
        logoutUser: (state) => {
            state.user = initialState.user;
        },
        getUsers: (state, action) => {
            state.users = action.payload;
        },
        clearUsers: (state) => {
            state.users = initialState.users;
        },
        getActiveInactiveUsers: (state, action) => {
            state.activeInactiveUsers = action.payload;
        },
        clearActiveInactiveUsers: (state) => {
            state.activeInactiveUsers = initialState.activeInactiveUsers;
        },
        getUserAdmin: (state, action) => {
            state.userAdmin = action.payload;
        },
        clearUserAdmin: (state) => {
            state.userAdmin = initialState.userAdmin;
        },
        getRegistrationInfo: (state, action) => {
            state.registrationInfo = action.payload;
        },
        clearRegistrationInfo: (state) => {
            state.registrationInfo = initialState.registrationInfo;
        },
    },
});

export const logIn = async (credentials, dispatch) => {
    try {
        await logOut(dispatch);
        const response = await authEndpoints.logIn(credentials);
        if (!response.access_token) {
            throw new Error('Token not found');
        }
        auth.setToken(response.access_token);
        await getLoggedInUser(dispatch);
        dispatch(setFromLogin(true));
        return true;
    } catch (error) {
        return false;
    }
};

export const logOut = async (dispatch) => {
    dispatch(setFromLogin(false));
    dispatch(clearServices());
    dispatch(clearActiveCompany());
    dispatch(clearUserAdmin());
    dispatch(clearRegistrationInfo());
    dispatch(clearUsers());
    dispatch(clearActiveInactiveUsers());
    dispatch(clearCompanyInfractions());
    dispatch(clearCompanyLicenses());
    dispatch(clearCompanyLines());
    dispatch(clearAllLines());
    dispatch(clearSelectedLineId());
    dispatch(clearSelectedEquipmentType());
    dispatch(clearSelectedEquipmentClassification());
    dispatch(clearSelectedSubstituteEquipmentType());
    dispatch(clearSelectedSubstituteEquipmentClassification());
    dispatch(clearLineScheduleBoard());
    dispatch(clearSchedulesStore());
    dispatch(clearPermissionaries());
    dispatch(clearCompanyVehicles());
    dispatch(clearRequests());
    dispatch(logoutUser());
    auth.deleteToken();
    await auth.deleteUser();
};

export const getLoggedInUser = async (dispatch) => {
    try {
        const response = await authEndpoints.getLoggedInUser();
        if (!response || response.status !== 200 || !response.data) {
            throw new Error('User not found');
        }
        const user = response.data;
        auth.setUser(user);
        if (user.username) {
            await dispatch(fetchRegistrationInformation(user.username));
        }
        if (user.empresas && user.empresas.length > 0) {
            let cdEmpresa = user.cdEmpresaSelecionada ? user.cdEmpresaSelecionada : user.empresas[0].cdEmpresa;
            const response = await companyEndpoints.getCompany({ cdEmpresa: cdEmpresa });
            if (response.status === 200 && response.data) {
                await dispatch(setActiveCompany(response.data));
            }
        }
        await dispatch(getUser(user));
    } catch (error) {
        await logOut(dispatch);
        dispatch(setFromLogin(false));
    }
};

export const fetchUsers = () => async (dispatch) => {
    try {
        const response = await userEndpoints.getUsers();
        dispatch(getUsers(response.data));
    } catch (error) {
        errorHandler(error.response?.status, error.response?.data?.message);
    }
};

export const fetchActiveInactiveUsers = () => async (dispatch) => {
    try {
        const response = await userEndpoints.getActiveInactiveUsers();
        dispatch(getActiveInactiveUsers(response.data));
    } catch (error) {
        errorHandler(error.response?.status, error.response?.data?.message);
    }
};

export const fetchRegistrationInformation = (cdUsuario) => async (dispatch) => {
    try {
        const response = await userEndpoints.getUserInformations(cdUsuario);
        dispatch(getRegistrationInfo(response.data));
    } catch (error) {
        errorHandler(error.response?.status, error.response?.data?.message);
    }
};

export const fetchUserAdmin = (cdUsuario) => async (dispatch) => {
    try {
        const response = await userEndpoints.getUserInformations(cdUsuario);
        dispatch(getUserAdmin(response.data));
    } catch (error) {}
};

export const {
    getUser,
    logoutUser,
    getUsers,
    clearUsers,
    getActiveInactiveUsers,
    clearActiveInactiveUsers,
    getRegistrationInfo,
    clearRegistrationInfo,
    getUserAdmin,
    clearUserAdmin,
} = userSlice.actions;

export default userSlice;
